import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";
import type { HeroVorgangRailData } from "./hero-vorgang-detail-rail";
import type { HeroVorgangMessageData } from "./hero-vorgang-message";
import type { HeroUnknownContactData } from "./hero-vorgang-unknown-panel";

export type HeroVorgangConversationId =
  | "michael-bauer"
  | "sabine-krueger"
  | "klaus-richter";

export type HeroVorgangThreadItem =
  | { readonly kind: "message"; readonly message: HeroVorgangMessageData }
  | {
      readonly kind: "system_log";
      readonly text: string;
      readonly date: string;
    }
  | { readonly kind: "attachment"; readonly fileName: string };

export type HeroVorgangFixture = {
  readonly id: HeroVorgangConversationId;
  readonly headerTitle: string;
  readonly thread: readonly HeroVorgangThreadItem[];
  readonly rail: HeroVorgangRailData | null;
  readonly unknownContact: HeroUnknownContactData | null;
};

const SABINE_MSG1 = `Guten Tag,

wir bräuchten einen Termin für die jährliche Heizungswartung in unserer Wohnung.
Könnten Sie uns bitte Optionen für die nächsten zwei Wochen nennen?

Freundliche Grüße
Sabine Krüger`;

const SABINE_MSG2 = `Hallo Frau Krüger,

vielen Dank für Ihre Nachricht. Gerne stimmen wir einen Termin ab —
Donnerstag oder Freitag nächste Woche wäre bei uns möglich.

Mit freundlichen Grüßen
Jonathan Tausendfreund
Power Prozesse GmbH

-------- Ursprüngliche Nachricht --------
Von: Sabine Krüger <sabine.krueger@example.de>
Gesendet: Montag, 10. Juni 2026, 09:14
An: jonathan@hausverwalter.ai
Betreff: Heizungswartung abstimmen

Guten Tag,

wir bräuchten einen Termin für die jährliche Heizungswartung in unserer Wohnung.
Könnten Sie uns bitte Optionen für die nächsten zwei Wochen nennen?

Freundliche Grüße
Sabine Krüger`;

const SABINE_MSG4 = `Donnerstag, 13.06. passt mir am besten — ab 14 Uhr bin ich zu Hause.

Freundliche Grüße
Sabine Krüger

Am 11.06.2026 um 10:22 schrieb Jonathan Tausendfreund <jonathan@hausverwalter.ai>:

> Hallo Frau Krüger,
>
> vielen Dank für Ihre Nachricht. Gerne stimmen wir einen Termin ab —
> Donnerstag oder Freitag nächste Woche wäre bei uns möglich.
>
> Mit freundlichen Grüßen
> Jonathan Tausendfreund
> Power Prozesse GmbH
>
> -------- Ursprüngliche Nachricht --------
> Von: Sabine Krüger <sabine.krueger@example.de>
> Gesendet: Montag, 10. Juni 2026, 09:14
> An: jonathan@hausverwalter.ai
> Betreff: Heizungswartung abstimmen
>
> Guten Tag,
>
> wir bräuchten einen Termin für die jährliche Heizungswartung in unserer Wohnung.
> Könnten Sie uns bitte Optionen für die nächsten zwei Wochen nennen?
>
> Freundliche Grüße
> Sabine Krüger`;

const SABINE_MSG5 = `Perfekt, ich trage Donnerstag, 13.06., 14:00 Uhr ein.
Unser Dienstleister meldet sich kurz vorher telefonisch an.

Mit freundlichen Grüßen
Jonathan Tausendfreund

Am 12.06.2026 um 08:05 schrieb Sabine Krüger <sabine.krueger@example.de>:

> Donnerstag, 13.06. passt mir am besten — ab 14 Uhr bin ich zu Hause.
>
> Freundliche Grüße
> Sabine Krüger
>
> Am 11.06.2026 um 10:22 schrieb Jonathan Tausendfreund <jonathan@hausverwalter.ai>:
>
> > Hallo Frau Krüger,
> >
> > vielen Dank für Ihre Nachricht. Gerne stimmen wir einen Termin ab —
> > Donnerstag oder Freitag nächste Woche wäre bei uns möglich.
> >
> > Mit freundlichen Grüßen
> > Jonathan Tausendfreund
> > Power Prozesse GmbH
> >
> > -------- Ursprüngliche Nachricht --------
> > Von: Sabine Krüger <sabine.krueger@example.de>
> > Gesendet: Montag, 10. Juni 2026, 09:14
> > An: jonathan@hausverwalter.ai
> > Betreff: Heizungswartung abstimmen
> >
> > Guten Tag,
> >
> > wir bräuchten einen Termin für die jährliche Heizungswartung in unserer Wohnung.
> > Könnten Sie uns bitte Optionen für die nächsten zwei Wochen nennen?
> >
> > Freundliche Grüße
> > Sabine Krüger`;

const KLAUS_MSG0 = `Hallo Herr Richter,

vielen Dank für Ihre Meldung zum Wasserschaden in der Küche. Wir haben
den Schaden aufgenommen und melden uns mit den nächsten Schritten.

Mit freundlichen Grüßen
Jonathan Tausendfreund`;

const KLAUS_MSG1 = `Hallo Jonathan,

unter der Spüle in der Küche tropft es seit heute Morgen deutlich stärker.
Könnten Sie bitte einen Sanitär beauftragen und mir sagen, ob ich
Schadensfotos an die Versicherung schicken soll?

Viele Grüße
Klaus Richter

-------- Ursprüngliche Nachricht --------
Von: Jonathan Tausendfreund <jonathan@hausverwalter.ai>
Gesendet: 08.06.2026, 15:40
An: klaus.richter@example.de
Betreff: Wasserschaden in der Küche

Hallo Herr Richter,

vielen Dank für Ihre Meldung zum Wasserschaden in der Küche. Wir haben
den Schaden aufgenommen und melden uns mit den nächsten Schritten.

Mit freundlichen Grüßen
Jonathan Tausendfreund`;

const KLAUS_MSG3 = `Hallo Herr Richter,

ich habe soeben unseren Sanitär beauftragt — Termin morgen Vormittag.
Ja, bitte senden Sie 2–3 Fotos direkt an mich, ich leite sie weiter.

Mit freundlichen Grüßen
Jonathan Tausendfreund

Am 09.06.2026 um 11:18 schrieb Klaus Richter <klaus.richter@example.de>:

> Hallo Jonathan,
>
> unter der Spüle in der Küche tropft es seit heute Morgen deutlich stärker.
> Könnten Sie bitte einen Sanitär beauftragen und mir sagen, ob ich
> Schadensfotos an die Versicherung schicken soll?
>
> Viele Grüße
> Klaus Richter
>
> -------- Ursprüngliche Nachricht --------
> Von: Jonathan Tausendfreund <jonathan@hausverwalter.ai>
> Gesendet: 08.06.2026, 15:40
> An: klaus.richter@example.de
> Betreff: Wasserschaden in der Küche
>
> Hallo Herr Richter,
>
> vielen Dank für Ihre Meldung zum Wasserschaden in der Küche. Wir haben
> den Schaden aufgenommen und melden uns mit den nächsten Schritten.
>
> Mit freundlichen Grüßen
> Jonathan Tausendfreund`;

const KLAUS_MSG4 = `Fotos sind im Anhang. Der Tropf ist jetzt fast durchgehend.

Klaus Richter

Am 09.06.2026 um 14:02 schrieb Jonathan Tausendfreund <jonathan@hausverwalter.ai>:

> Hallo Herr Richter,
>
> ich habe soeben unseren Sanitär beauftragt — Termin morgen Vormittag.
> Ja, bitte senden Sie 2–3 Fotos direkt an mich, ich leite sie weiter.
>
> Mit freundlichen Grüßen
> Jonathan Tausendfreund
>
> Am 09.06.2026 um 11:18 schrieb Klaus Richter <klaus.richter@example.de>:
>
> > Hallo Jonathan,
> >
> > unter der Spüle in der Küche tropft es seit heute Morgen deutlich stärker.
> > Könnten Sie bitte einen Sanitär beauftragen und mir sagen, ob ich
> > Schadensfotos an die Versicherung schicken soll?
> >
> > Viele Grüße
> > Klaus Richter
> >
> > -------- Ursprüngliche Nachricht --------
> > Von: Jonathan Tausendfreund <jonathan@hausverwalter.ai>
> > Gesendet: 08.06.2026, 15:40
> > An: klaus.richter@example.de
> > Betreff: Wasserschaden in der Küche
> >
> > Hallo Herr Richter,
> >
> > vielen Dank für Ihre Meldung zum Wasserschaden in der Küche. Wir haben
> > den Schaden aufgenommen und melden uns mit den nächsten Schritten.
> >
> > Mit freundlichen Grüßen
> > Jonathan Tausendfreund`;

function michaelBauer(t: Translate): HeroVorgangFixture {
  return {
    id: "michael-bauer",
    headerTitle: t("fensterLaesstNichtSchliessen"),
    unknownContact: null,
    rail: {
      externalId: "#2026-0891",
      title: t("fensterLaesstNichtSchliessen"),
      statusLabel: "TO-DO",
      assignee: { initials: "NH", name: t("ninaHartmann"), bg: ld("#0B0F19", "#fafafa") },
      board: { name: t("schaeden"), icon: "alert" },
      recipient: { name: t("michaelBauer"), source: t("portal") },
      property: { name: t("ahornweg5"), unit: "WE 14" },
      openPickers: ["due", "priority"],
    },
    thread: [
      {
        kind: "message",
        message: {
          id: "mb-1",
          direction: "inbound",
          channel: "portal",
          authorName: "Michael Bauer",
          authorInitials: "MB",
          authorAvatarBg: ld("#E7EEF8", "#2c323a"),
          authorAvatarFg: ld("#3F6CB0", "#7cacf5"),
          roleLabel: t("mieterWe14"),
          timestamp: t("heute0815"),
          fullPlain: t("gutenTagSchlafzimmerKlemmt"),
        },
      },
      {
        kind: "system_log",
        text: t("ninaHartmannThemaSchaeden"),
        date: "heute",
      },
      {
        kind: "message",
        message: {
          id: "mb-2",
          direction: "inbound",
          channel: "portal",
          authorName: "Michael Bauer",
          authorInitials: "MB",
          authorAvatarBg: ld("#E7EEF8", "#2c323a"),
          authorAvatarFg: ld("#3F6CB0", "#7cacf5"),
          roleLabel: t("mieterWe14"),
          timestamp: t("heute0942"),
          fullPlain: t("regnetGeradeWiederRein"),
        },
      },
    ],
  };
}

function sabineKrueger(t: Translate): HeroVorgangFixture {
  return {
    id: "sabine-krueger",
    headerTitle: t("heizungswartungAbstimmen"),
    unknownContact: null,
    rail: {
      externalId: "#2026-0744",
      title: t("heizungswartungAbstimmen"),
      statusLabel: "TO-DO",
      assignee: { initials: "NH", name: t("ninaHartmann"), bg: ld("#0B0F19", "#fafafa") },
      board: { name: t("wartungen"), icon: "inbox" },
      recipient: { name: t("sabineKrueger"), source: t("eMail") },
      property: null,
      openPickers: ["object", "due", "tags"],
    },
    thread: [
      {
        kind: "message",
        message: {
          id: "sk-1",
          direction: "inbound",
          channel: "email",
          subject: t("heizungswartungAbstimmen"),
          fromLine: t("sabineKruegerSabineKrueger"),
          authorName: t("sabineKrueger"),
          authorInitials: "SK",
          authorAvatarBg: ld("#EEE9F7", "#35303c"),
          authorAvatarFg: ld("#6D5BA6", "#ae9ded"),
          timestamp: "10.06.2026, 09:14",
          fullPlain: SABINE_MSG1,
        },
      },
      {
        kind: "message",
        message: {
          id: "sk-2",
          direction: "outbound",
          channel: "email",
          subject: t("awHeizungswartungAbstimmen"),
          fromLine: "Jonathan Tausendfreund <jonathan@hausverwalter.ai>",
          authorName: "Jonathan Tausendfreund",
          authorInitials: "JT",
          authorAvatarBg: ld("#0B0F19", "#fafafa"),
          authorAvatarFg: ld("#fff", "#181b1f"),
          timestamp: "11.06.2026, 10:22",
          fullPlain: SABINE_MSG2,
          signatureName: "Jonathan",
        },
      },
      {
        kind: "system_log",
        text: t("jonathanTausendfreundBeschreibungGeaendert"),
        date: "11.6.2026",
      },
      {
        kind: "message",
        message: {
          id: "sk-4",
          direction: "inbound",
          channel: "email",
          subject: t("awHeizungswartungAbstimmen"),
          fromLine: t("sabineKruegerSabineKrueger"),
          authorName: t("sabineKrueger"),
          authorInitials: "SK",
          authorAvatarBg: ld("#EEE9F7", "#35303c"),
          authorAvatarFg: ld("#6D5BA6", "#ae9ded"),
          timestamp: "12.06.2026, 08:05",
          fullPlain: SABINE_MSG4,
        },
      },
      {
        kind: "message",
        message: {
          id: "sk-5",
          direction: "outbound",
          channel: "email",
          subject: t("awHeizungswartungAbstimmen"),
          fromLine: "Jonathan Tausendfreund <jonathan@hausverwalter.ai>",
          authorName: "Jonathan Tausendfreund",
          authorInitials: "JT",
          authorAvatarBg: ld("#0B0F19", "#fafafa"),
          authorAvatarFg: ld("#fff", "#181b1f"),
          timestamp: "12.06.2026, 11:30",
          fullPlain: SABINE_MSG5,
          signatureName: "Jonathan",
        },
      },
    ],
  };
}

function klausRichter(t: Translate): HeroVorgangFixture {
  return {
    id: "klaus-richter",
    headerTitle: t("wasserschadenKueche"),
    unknownContact: null,
    rail: {
      externalId: "#2026-1012",
      title: t("wasserschadenKueche"),
      statusLabel: "TO-DO",
      assignee: { initials: "TB", name: t("tobiasBach"), bg: ld("#0B0F19", "#fafafa") },
      board: { name: t("schaeden"), icon: "alert" },
      recipient: { name: t("klausRichter"), source: t("eMail") },
      property: { name: t("birkenallee12"), unit: "WE 3 · EG" },
      openPickers: ["priority", "description"],
    },
    thread: [
      {
        kind: "message",
        message: {
          id: "kr-0",
          direction: "outbound",
          channel: "email",
          subject: t("wasserschadenKueche"),
          fromLine: "Jonathan Tausendfreund <jonathan@hausverwalter.ai>",
          authorName: "Jonathan Tausendfreund",
          authorInitials: "JT",
          authorAvatarBg: ld("#0B0F19", "#fafafa"),
          authorAvatarFg: ld("#fff", "#181b1f"),
          timestamp: "08.06.2026, 15:40",
          fullPlain: KLAUS_MSG0,
          signatureName: "Jonathan",
        },
      },
      {
        kind: "message",
        message: {
          id: "kr-1",
          direction: "inbound",
          channel: "email",
          subject: t("awWasserschadenKueche"),
          fromLine: "Klaus Richter <klaus.richter@example.de>",
          authorName: "Klaus Richter",
          authorInitials: "KR",
          authorAvatarBg: ld("#F6E9EE", "#392e33"),
          authorAvatarFg: ld("#B05B79", "#e68ba9"),
          timestamp: "09.06.2026, 11:18",
          fullPlain: KLAUS_MSG1,
        },
      },
      {
        kind: "system_log",
        text: t("jonathanTausendfreundBeschreibungGeaendert"),
        date: "9.6.2026",
      },
      {
        kind: "message",
        message: {
          id: "kr-3",
          direction: "outbound",
          channel: "email",
          subject: t("reAwWasserschadenKueche"),
          fromLine: "Jonathan Tausendfreund <jonathan@hausverwalter.ai>",
          authorName: "Jonathan Tausendfreund",
          authorInitials: "JT",
          authorAvatarBg: ld("#0B0F19", "#fafafa"),
          authorAvatarFg: ld("#fff", "#181b1f"),
          timestamp: "09.06.2026, 14:02",
          fullPlain: KLAUS_MSG3,
          signatureName: "Jonathan",
        },
      },
      {
        kind: "message",
        message: {
          id: "kr-4",
          direction: "inbound",
          channel: "email",
          subject: t("reAwWasserschadenKueche"),
          fromLine: "Klaus Richter <klaus.richter@example.de>",
          authorName: "Klaus Richter",
          authorInitials: "KR",
          authorAvatarBg: ld("#F6E9EE", "#392e33"),
          authorAvatarFg: ld("#B05B79", "#e68ba9"),
          timestamp: "09.06.2026, 16:45",
          fullPlain: KLAUS_MSG4,
        },
      },
      { kind: "attachment", fileName: "Schaden_Kueche.pdf" },
      {
        kind: "system_log",
        text: t("jonathanTausendfreundStatusTo"),
        date: "10.6.2026",
      },
    ],
  };
}

function vorgaenge(t: Translate): readonly HeroVorgangFixture[] {
  return [michaelBauer(t), sabineKrueger(t), klausRichter(t)];
}

export function getVorgangById(
  t: Translate,
  id: HeroVorgangConversationId,
): HeroVorgangFixture | null {
  const match: HeroVorgangFixture | undefined = vorgaenge(t).find(
    (v: HeroVorgangFixture) => v.id === id,
  );
  return match ?? null;
}

export function isHeroVorgangConversationId(
  value: string,
): value is HeroVorgangConversationId {
  return (
    value === "michael-bauer" ||
    value === "sabine-krueger" ||
    value === "klaus-richter"
  );
}
